import { can, formatDate } from '@clim/client-core';
import {
  COMMISSION_KIND_LABELS,
  COMMISSION_STATUS_LABELS,
  commissionTotals,
  formatEuros,
  periodLabel,
  shiftPeriod,
  type CommissionRecord,
  type CommissionStatus,
} from '@clim/domain';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { downloadFile } from '../lib/download';
import { errorMessage } from '../lib/errors';
import { useReady } from '../lib/session';
import { useOnline } from '../lib/use-online';
import { ErrorBox } from './ui';

export const STATUS_TONE: Record<CommissionStatus, string> = { to_validate: 'warning', validated: 'info', paid: 'success', cancelled: 'neutral' };
export const kindShort = (c: Pick<CommissionRecord, 'kind' | 'activity'>) =>
  c.kind === 'sale' ? 'Vente' : c.kind === 'install' ? (c.activity === 'entretien' ? 'Entretien' : 'Pose') : c.kind === 'team' ? 'Encadrement' : 'Ajustement';

export function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function PeriodPicker({ period, onChange }: { period: string; onChange(p: string): void }) {
  return (
    <div className="period">
      <button className="btn small" aria-label="Mois précédent" onClick={() => onChange(shiftPeriod(period, -1))}>
        ◀
      </button>
      <strong>{periodLabel(period)}</strong>
      <button className="btn small" aria-label="Mois suivant" onClick={() => onChange(shiftPeriod(period, 1))}>
        ▶
      </button>
    </div>
  );
}

/** Vue permanente du mois (§11) : total, à valider, validé, payé. */
export function Totals({ entries }: { entries: CommissionRecord[] }) {
  const t = commissionTotals(entries);
  // rémunérations des chantiers partenaires (sous-traitance de pose), distinguées du reste
  const partner = commissionTotals(entries.filter((e) => e.partnerName));
  return (
    <dl className="money commission-totals" aria-label="Totaux du mois">
      <div className="balance">
        <dt>Total du mois</dt>
        <dd>{formatEuros(t.totalCents)}</dd>
      </div>
      <div>
        <dt>À valider</dt>
        <dd>{formatEuros(t.toValidateCents)}</dd>
      </div>
      <div>
        <dt>Validé</dt>
        <dd>{formatEuros(t.validatedCents)}</dd>
      </div>
      <div>
        <dt>Payé</dt>
        <dd>{formatEuros(t.paidCents)}</dd>
      </div>
      {partner.totalCents !== 0 && (
        <div>
          <dt>dont sous-traitance partenaires</dt>
          <dd>{formatEuros(partner.totalCents)}</dd>
        </div>
      )}
    </dl>
  );
}

export function ExportButtons({ period, query = '' }: { period: string; query?: string }) {
  const { me, api } = useReady();
  const online = useOnline();
  const [error, setError] = useState<string | null>(null);
  if (!can(me, 'commission.export')) return null;
  const run = async (format: 'csv' | 'pdf') => {
    setError(null);
    try {
      const data = await api.request<string | Uint8Array>('GET', `/v1/commissions/export?period=${period}&format=${format}${query}`);
      downloadFile(data, `releve-commissions-${period}.${format}`, format === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8');
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <>
      <div className="actions wrap">
        <button className="btn small" disabled={!online} onClick={() => void run('csv')}>
          ⭳ Export tableur (CSV)
        </button>
        <button className="btn small" disabled={!online} onClick={() => void run('pdf')}>
          ⭳ Relevé PDF
        </button>
      </div>
      <ErrorBox message={error} />
    </>
  );
}

/** Détail par dossier / client (§11). */
export function EntryList({ entries, selectable, selected, onToggle, showBeneficiary }: { entries: CommissionRecord[]; selectable?: boolean; selected?: Set<string>; onToggle?(id: string): void; showBeneficiary?: boolean }) {
  if (!entries.length) return <p className="empty">Aucune ligne pour cette période.</p>;
  return (
    <div className="table-wrap">
      <table className="table commissions">
        <thead>
          <tr>
            {selectable && <th aria-label="Sélection" />}
            <th>Date</th>
            {showBeneficiary && <th>Bénéficiaire</th>}
            <th>Dossier / client</th>
            <th>Type</th>
            <th>Produit / prestation</th>
            <th className="num">Montant</th>
            <th>Statut</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((c) => (
            <tr key={c.id} className={c.status === 'cancelled' ? 'disabled' : ''}>
              {selectable && (
                <td>
                  <input type="checkbox" aria-label={`Sélectionner ${c.label}`} checked={selected?.has(c.id) ?? false} onChange={() => onToggle?.(c.id)} disabled={c.status === 'paid' || c.status === 'cancelled'} />
                </td>
              )}
              <td className="nowrap">{c.earnedOn.split('-').reverse().join('/')}</td>
              {showBeneficiary && <td>{c.beneficiaryName}</td>}
              <td>
                {c.dossierId ? <Link to={`/dossiers/${c.dossierId}`}>{c.dossierReference ?? '—'}</Link> : '—'}
                <div className="muted small">{c.clientName ?? ''}</div>
                {c.partnerName && <span className="badge partner">Partenaire : {c.partnerName}</span>}
                {c.crewInterventionId && c.status === 'to_validate' && (
                  <span className="badge tone-warning" title="Intervention réalisée à plusieurs : montant à répartir entre les techniciens">
                    À répartir entre techniciens
                  </span>
                )}
              </td>
              <td>
                <span className={`kind kind-${c.kind === 'sale' ? 'vente' : c.kind === 'install' ? 'pose' : c.kind === 'team' ? 'team' : 'adj'}`}>{kindShort(c)}</span>
              </td>
              <td>
                {c.label}
                {c.comment && <div className="muted small">{c.comment}</div>}
                {c.paymentReference && <div className="muted small">Payé : {c.paymentReference}</div>}
              </td>
              <td className={`num nowrap${c.amountCents < 0 ? ' negative' : ''}`}>{formatEuros(c.amountCents)}</td>
              <td>
                <span className={`badge tone-${STATUS_TONE[c.status]}`} title={c.validatedByName ? `Validé par ${c.validatedByName}${c.validatedAt ? ` le ${formatDate(c.validatedAt)}` : ''}` : undefined}>
                  {COMMISSION_STATUS_LABELS[c.status]}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const KIND_LABELS = COMMISSION_KIND_LABELS;
